/**
 * Iter276-suppliers — Suppliers admin page.
 * Full CRUD for `supplier_letterheads` (list + inline edit + create + logo upload).
 * Reachable from the Materials Audit Fix modal "Edit supplier details →" link
 * and from the sidebar.
 * Route: /admin/suppliers?slug={optional}
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Truck, Plus, Save, Loader2, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const BLANK_LETTERHEAD = {
  slug: '', display_name: '', short_name: '', aka: [],
  address: '', phone: '', email: '', account_number: '',
  accent_color: '#1F2937', notes: '', logo_filename: null,
};

export default function SuppliersAdminPage() {
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  const highlightSlug = searchParams.get('slug');

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // full doc being edited
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/supplier-letterheads');
      const items = data?.letterheads || data || [];
      setList(items);
      // Auto-open the highlighted slug once list is loaded.
      if (highlightSlug) {
        const hit = items.find((s) => s.slug === highlightSlug);
        if (hit) setEditing(hit);
        else {
          // Prefill create form with the missing slug so users can add it.
          setCreating(true);
          setEditing({ ...BLANK_LETTERHEAD, slug: highlightSlug, display_name: highlightSlug });
        }
      }
    } catch (err) {
      toast.error(`Load failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [api, highlightSlug]);
  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    try {
      await api.put(`/supplier-letterheads/${payload.slug}`, payload);
      toast.success('Supplier saved');
      setEditing(null);
      setCreating(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete supplier letterhead "${slug}"? Order PDFs for this supplier will fall back to a plain letterhead.`)) return;
    try {
      await api.delete(`/supplier-letterheads/${slug}`);
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  return (
    <div className="p-4" data-testid="suppliers-admin-page">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Truck className="text-orange-600" size={20} /> Suppliers
          </h1>
          <p className="text-xs text-slate-500">
            Contact details, branding, and alternate names for each supplier. Drives the Materials Order PDF letterheads.
          </p>
        </div>
        <button
          onClick={() => { setCreating(true); setEditing({ ...BLANK_LETTERHEAD }); }}
          data-testid="suppliers-admin-add-btn"
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-orange-600 text-white hover:bg-orange-700"
        >
          <Plus size={12} /> Add supplier
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500 text-xs">
          <Loader2 size={16} className="animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2 text-left">Logo</th>
                <th className="px-3 py-2 text-left">Slug</th>
                <th className="px-3 py-2 text-left">Display · Short · AKA</th>
                <th className="px-3 py-2 text-left">Contact</th>
                <th className="px-3 py-2 text-left">A/C</th>
                <th className="px-3 py-2 text-left">Accent</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-slate-400 py-8 italic">No suppliers yet — click &quot;Add supplier&quot;.</td></tr>
              ) : list.map((s) => (
                <tr key={s.slug} className={`border-b border-slate-100 hover:bg-slate-50 ${highlightSlug === s.slug ? 'bg-orange-50' : ''}`} data-testid={`suppliers-admin-row-${s.slug}`}>
                  <td className="px-3 py-2">
                    {s.logo_url ? <img src={s.logo_url} alt="" className="h-6 max-w-[80px] object-contain" /> : <span className="text-slate-400 italic text-[10px]">no logo</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px]">{s.slug}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-800">{s.display_name}</div>
                    <div className="text-[10px] text-slate-500">
                      {s.short_name && <span className="mr-1">short: <b>{s.short_name}</b></span>}
                      {(s.aka || []).length > 0 && <span>aka: {(s.aka || []).join(', ')}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div>{s.phone || <span className="text-slate-400">—</span>}</div>
                    <div className="text-[10px] text-slate-500">{s.email || '—'}</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px]">{s.account_number || '—'}</td>
                  <td className="px-3 py-2"><span className="inline-block w-4 h-4 rounded border border-slate-300" style={{ background: s.accent_color }} /></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => { setEditing(s); setCreating(false); }} className="text-[10px] px-1.5 py-0.5 rounded border border-slate-300 bg-white hover:bg-slate-50 mr-1" data-testid={`suppliers-admin-edit-${s.slug}`}>Edit</button>
                    <button onClick={() => remove(s.slug)} className="text-[10px] px-1.5 py-0.5 rounded border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100" data-testid={`suppliers-admin-delete-${s.slug}`}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <SupplierEditModal
          api={api}
          initial={editing}
          creating={creating}
          existingList={list.filter((s) => s.slug !== editing.slug)}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={save}
        />
      )}
    </div>
  );
}


function SupplierEditModal({ api, initial, creating, onClose, onSaved, existingList = [] }) {
  const [form, setForm] = useState({ ...initial, aka: initial.aka || [] });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [copyFrom, setCopyFrom] = useState('');

  // ─── Copy from existing supplier ───
  // Clones every field EXCEPT slug + display_name (those must remain
  // unique — the whole point of the new supplier is a fresh key).
  // Also skips logo_filename so we don't accidentally share logos.
  const doCopyFrom = (slug) => {
    setCopyFrom(slug);
    if (!slug) return;
    const src = existingList.find((s) => s.slug === slug);
    if (!src) return;
    setForm((f) => ({
      ...f,
      short_name:      src.short_name || '',
      aka:             [...(src.aka || [])],
      address:         src.address || '',
      phone:           src.phone || '',
      email:           src.email || '',
      account_number:  src.account_number || '',
      accent_color:    src.accent_color || '#1F2937',
      notes:           src.notes || '',
    }));
    toast.success(`Copied from ${src.display_name}`);
  };

  const submit = async () => {
    if (!(form.slug || '').trim() || !(form.display_name || '').trim()) {
      toast.error('Slug + display_name required');
      return;
    }
    setSaving(true);
    await onSaved(form);
    setSaving(false);
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/supplier-letterheads/${form.slug}/logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, logo_filename: data?.letterhead?.logo_filename }));
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="suppliers-admin-edit-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900">
            {creating ? 'Add supplier' : `Edit — ${form.display_name}`}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>

        {creating && existingList.length > 0 && (
          <div className="px-4 py-2 border-b border-slate-100 bg-sky-50 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-sky-800 font-bold">Prefill</span>
            <select
              value={copyFrom}
              onChange={(e) => doCopyFrom(e.target.value)}
              data-testid="suppliers-admin-copy-from"
              className="text-xs border border-sky-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-sky-400"
            >
              <option value="">— Copy from existing supplier —</option>
              {existingList.map((s) => (
                <option key={s.slug} value={s.slug}>{s.display_name} ({s.slug})</option>
              ))}
            </select>
            <span className="text-[10px] text-slate-500 italic">Copies address, phone, email, A/C, colour, notes, AKA. Keeps slug + name unique.</span>
          </div>
        )}

        <div className="p-4 grid grid-cols-2 gap-3 overflow-y-auto text-xs">
          <label>
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Slug (URL key)</span>
            <input type="text" value={form.slug} disabled={!creating} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} data-testid="suppliers-admin-edit-slug" className="w-full text-xs border border-slate-300 rounded px-2 py-1 disabled:bg-slate-100" />
          </label>
          <label>
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Display name *</span>
            <input type="text" value={form.display_name} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} data-testid="suppliers-admin-edit-display-name" className="w-full text-xs border border-slate-300 rounded px-2 py-1" />
          </label>
          <label>
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Short / nickname</span>
            <input type="text" value={form.short_name || ''} onChange={(e) => setForm((f) => ({ ...f, short_name: e.target.value }))} data-testid="suppliers-admin-edit-short-name" className="w-full text-xs border border-slate-300 rounded px-2 py-1" />
          </label>
          <label>
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Alternate names (comma-sep)</span>
            <input type="text" value={(form.aka || []).join(', ')} onChange={(e) => setForm((f) => ({ ...f, aka: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) }))} data-testid="suppliers-admin-edit-aka" className="w-full text-xs border border-slate-300 rounded px-2 py-1" placeholder="e.g. Bunnings Warehouse, Bunnings NZ" />
          </label>
          <label className="col-span-2">
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Address</span>
            <input type="text" value={form.address || ''} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} data-testid="suppliers-admin-edit-address" className="w-full text-xs border border-slate-300 rounded px-2 py-1" />
          </label>
          <label>
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Phone</span>
            <input type="text" value={form.phone || ''} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="suppliers-admin-edit-phone" className="w-full text-xs border border-slate-300 rounded px-2 py-1" />
          </label>
          <label>
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Email</span>
            <input type="email" value={form.email || ''} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="suppliers-admin-edit-email" className="w-full text-xs border border-slate-300 rounded px-2 py-1" />
          </label>
          <label>
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Account number</span>
            <input type="text" value={form.account_number || ''} onChange={(e) => setForm((f) => ({ ...f, account_number: e.target.value }))} data-testid="suppliers-admin-edit-account" className="w-full text-xs border border-slate-300 rounded px-2 py-1" />
          </label>
          <label>
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Brand accent</span>
            <div className="flex items-center gap-2">
              <input type="color" value={form.accent_color || '#1F2937'} onChange={(e) => setForm((f) => ({ ...f, accent_color: e.target.value }))} data-testid="suppliers-admin-edit-accent" className="h-7 w-14 border border-slate-300 rounded cursor-pointer" />
              <input type="text" value={form.accent_color || '#1F2937'} onChange={(e) => setForm((f) => ({ ...f, accent_color: e.target.value }))} className="w-24 text-xs border border-slate-300 rounded px-2 py-1 font-mono" />
            </div>
          </label>
          <label className="col-span-2">
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Notes (shown in PDF footer)</span>
            <textarea value={form.notes || ''} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="suppliers-admin-edit-notes" rows={2} className="w-full text-xs border border-slate-300 rounded px-2 py-1" />
          </label>

          <div className="col-span-2 flex items-center gap-3 pt-2 border-t border-slate-100">
            <div>
              <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-bold">Logo</span>
              {form.logo_filename ? (
                <img src={`/api/static/supplier_logos/${form.logo_filename}`} alt="" className="h-10 max-w-[120px] object-contain border border-slate-200 rounded p-1 bg-white" />
              ) : (
                <span className="text-[10px] text-slate-400 italic">no logo yet</span>
              )}
            </div>
            <label className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer" data-testid="suppliers-admin-edit-logo-upload">
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {form.logo_filename ? 'Replace logo' : 'Upload logo'}
              <input type="file" accept="image/*" onChange={(e) => uploadLogo(e.target.files?.[0])} disabled={!form.slug || uploading} className="hidden" />
            </label>
            {!form.slug && <span className="text-[10px] text-amber-600 italic">Save first, then upload a logo.</span>}
          </div>
        </div>

        <div className="px-4 py-3 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="suppliers-admin-edit-save" className="text-xs px-3 py-1.5 rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 flex items-center gap-1">
            {saving && <Loader2 size={12} className="animate-spin" />} <Save size={12} /> Save
          </button>
        </div>
      </div>
    </div>
  );
}
